import {defineStore} from "pinia";
import {AnnouncementDto, type AnnouncementSeverity} from "@/api/enterprise-gateway-api.proxy.ts";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";
import {LoadingState} from "@/app/stores/loading-state.ts";
import {useErrorStore} from "@/app/stores/error-store.ts";
import {ref, type Ref} from "vue";

export interface AnnouncementFormData {
    dateFrom: string[],
    dateTo: string[],
    title: string,
    message: string
}
class AnnouncementLoadingState extends LoadingState {
    private readonly _savingAnnouncement: Ref<boolean>;
    private readonly _isDeletingAnnouncement: Ref<boolean>;


    constructor() {
        super();
        this._savingAnnouncement = ref(false);
        this._isDeletingAnnouncement = ref(false);
    }

    get isSavingAnnouncement(): Ref<boolean> {
        return this._savingAnnouncement;
    }

    get isDeletingAnnouncement(): Ref<boolean> {
        return this._isDeletingAnnouncement;
    }


    public savingAnnouncement(): void {
        this._savingAnnouncement.value = true;
    }

    public stopSavingAnnouncement(): void {
        this._savingAnnouncement.value = false;
    }

    public deletingAnnouncement(): void {
        this._isDeletingAnnouncement.value = true;
    }

    public stopDeletingAnnouncement(): void {
        this._isDeletingAnnouncement.value = false;
    }
}

export const useAnnouncementStore = defineStore('announcement-store', () => {
    const apiProxy = useEnterpriseGatewayApiProxy();
    const errorStore = useErrorStore();
    const loadingState = new AnnouncementLoadingState();
    
    const saveAnnouncement = async (announcement: AnnouncementDto) => {
        try {
            loadingState.savingAnnouncement();
            const result = announcement.id 
                ? await apiProxy.announcement_Update(announcement.id, announcement) 
                : await apiProxy.announcement_Create(announcement);
            loadingState.stopSavingAnnouncement();
            return result;
        }  catch (e) {
            await errorStore.setError(e, true);
            loadingState.setError();
        }
    }
    
    const deleteAnnouncement = async (announcementId: string) => {
        try {
            loadingState.deletingAnnouncement()
            await apiProxy.announcement_Delete(announcementId);
            loadingState.stopDeletingAnnouncement();
        } catch (e) {
            await errorStore.setError(e, true);
            loadingState.setError();
        }
    }
    
    const loadAnnouncements = async () : Promise<AnnouncementDto[] | undefined> => {
        try {
            loadingState.startLoading();
            const result = apiProxy.announcement_GetAll();
            loadingState.stopLoading();
            return result;
        } catch (e) {
            await errorStore.setError(e, true);
            loadingState.setError();
        }
    }
    
    const getActive = async () : Promise<AnnouncementDto[] | undefined> => {
        try {
            loadingState.startLoading();
            const result = apiProxy.announcement_GetActive();
            loadingState.stopLoading();
            return result;
        } catch (e) {
            await errorStore.setError(e, true);
            loadingState.setError();
        }
    }
    
    return {
        isLoading: loadingState.isLoading,
        isSavingAnnouncement: loadingState.isSavingAnnouncement,
        isDeletingAnnouncement: loadingState.isDeletingAnnouncement,
        saveAnnouncement,
        deleteAnnouncement,
        loadAnnouncements,
        getActive
    }
});